import { Component, Input, ChangeDetectionStrategy, Injector, TemplateRef, QueryList, ContentChildren } from '@angular/core'
import { Store } from '@ngrx/store'
import { NavController } from 'ionic-angular'
import { UIEventHub } from '../../../services/event.service'
import { ActionCreator } from '../../../services/action.service'
import { ViewModel } from '../../../view-model'
import { BaseWidget } from '../../base-widget'
import { BaseFixedTableComponent, TimerUtil } from '@ar2/common';
import { ChangeEvent } from 'angular2-virtual-scroll';
import { AfterViewInit, ViewChild, ElementRef, NgZone, HostListener } from '@angular/core';

export interface ITableColGroup {
  index: number,
  width:any,
  conditions:boolean,
  sort:boolean,
  startAcdn:boolean,
  endAcdn:boolean
}

export interface IHeaderLayout {
  colIndex:number,
  colSpan: number,
  rowSpan: number,
  title: string,
  sort: boolean,
  startAcdn: boolean,
  endAcdn: boolean,
  parent: boolean,
  all: boolean
}
/**
 * 行列固定テーブル
 *【内容:records:any[]】
 *【カラムグループ:colGroup:ITableColGroup[]】
 *【ヘッダー:header:string[][]】
 *【固定カラム:fixationIndex:number】
 *【ソート状態:sort_status:{key:string,sort_order:boolean}[]】
 *【表示条件:visible:boolean】
 *【取得件数:limit:number】
 *【全件選択ボタンカラム:allSelected:number[]】
 *【行ハイライト:rowHighlight:boolean】
 *【0件結果:noResult:string】
 *【0件説明:description:string】
 *【フィルター条件:filterCondition:{[key:string]:any[]}】
 *【バーチャルスクロール:isVirtualScroll:boolean = true】
 *【追加内容:addRecords:any[]】
 *【削除内容:removeRecords:any[]】
 *【スモール行高:showSmallHeight:boolean】
 *【表示行数:displayRowCount:number】
 * @author nabla2.metamodel.generator
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'app-fixation-table',
  templateUrl: './fixation-table.html',
  styles: [`
    
  `]
})
export class FixationTable extends BaseFixedTableComponent implements AfterViewInit {
  /**
   * 子要素
   */
  children:TemplateRef<any>[]

  @ContentChildren(TemplateRef) templateRefs:QueryList<TemplateRef<any>>

  ngAfterContentInit() {
    this.children = this.templateRefs.toArray()
  }
  /**
   * ソート状態
   */
  @Input() sort_status:{key:string,sort_order:boolean}[]

  /**
   * 表示条件
   */
  @Input() visible:boolean

  /**
   * 取得件数
   */
  @Input() limit:number

  /**
   * 全件選択ボタンカラム
   */
  @Input() allSelected:number[]

  /**
   * 行ハイライト
   */
  @Input() rowHighlight:boolean

  /**
   * 0件結果
   */
  @Input() noResult:string

  /**
   * 0件説明
   */
  @Input() description:string

  /**
   * バーチャルスクロール
   */
  @Input() isVirtualScroll:boolean = true

  /**
   * スモール行高
   */
  @Input() showSmallHeight:boolean

  /**
   * 表示行数
   */
  @Input() displayRowCount:number

  /**
   * コンストラクタ
   * @param navCtrl 画面遷移管理サービス
   * @param store Reduxストア
   */
  constructor(
    public navCtrl: NavController,
    public store: Store<{viewModel:ViewModel}>,
    public eventHub: UIEventHub,
    public actionCreator: ActionCreator,
    public injector: Injector,
    public ngZone: NgZone,
  ) {
    super(injector)
  }
  /**
   * timer util settings
   *
   * @static
   * @memberof FixationTable
   */
  private timeUtil: TimerUtil = this.injector.get(TimerUtil);
  /**
   * left column group children
   *
   * @static
   * @memberof FixationTable
   */
  leftChildren: TemplateRef<any>[] = [];
  /**
   * right column group chidren
   *
   * @static
   * @memberof FixationTable
   */
  rightChildren: TemplateRef<any>[] = [];
  /**
   * width of left table
   *
   * @static
   * @memberof FixationTable
   */
  private _leftWidth: number = 0;
  /**
   * width of right table
   *
   * @static
   * @memberof FixationTable
   */
  private _rightWidth:number = 0;
  /**
   * width unit of left table
   *
   * @static
   * @memberof FixationTable
   */
  private _leftUnit: string;
  /**
   * width unit of right table
   *
   * @static
   * @memberof FixationTable
   */
  private _rightUnit: string;
  
  /**
   * 内容
   */
  @Input()
  set records(records: any[]) {
    this.startIndex = 0
    this._records = records
    this._rowSelected = null
    this.applyFilter()
    this.setScrollBar()
  }
  get records():any[] {
    return this._records
  }
  private _records:any[] = []
  /**
   * 行追加
   */
  @Input()
  set addRecords(addRecords: any[]) {
    if (!Array.isArray(addRecords) || addRecords.length === 0) return
  
    Array.prototype.push.apply(this.records, addRecords)
    while (addRecords.length) addRecords.pop()
    this.timeUtil.timeout(0).subscribe(() => {
      this._rowSelected = null
      this.virtualScroll.element.nativeElement.scrollTop = this.virtualScroll.element.nativeElement.scrollHeight - this.virtualScroll.element.nativeElement.clientHeight
      this.virtualScroll.scrollInto(this._records[this._records.length - 1])
    })
    this.setScrollBar()
  }
  /**
   * 行削除
   */
  @Input()
  set removeRecords(removeRecords: any[]) {
    if (!Array.isArray(removeRecords) || removeRecords.length === 0) return
  
    const _removeRecords = removeRecords.filter(e => e['_deleted'] !== true)
    _removeRecords.forEach(e => {
      e['_deleted'] = true
    })
    this.timeUtil.timeout(0).subscribe(() => {
      this._rowSelected = null
      this.startIndex = (this.startIndex - _removeRecords.length) > 0 ? this.startIndex - _removeRecords.length : 0
    })
    this.setScrollBar()
  }
  /**
   * フィルター条件
   */
  @Input() set filterCondition(cond:{[key:string]:string[]}) {
    this._filterCondition = cond
    this.applyFilter()
  }
  get filterCondition():{[key:string]:string[]} {
    return this._filterCondition
  }
  private _filterCondition:{[key:string]:string[]} = {}
  
  applyFilter():any[] {
    if (!this.filterCondition) {
      this.tableData = this.records.map(row => {
        row._showFlg = true
        return row
      })
      return
    }
    const keys = Object.keys(this.filterCondition)
    if (!keys.length) {
      this.tableData = this.records
      return
    }
    this.tableData = this.records.map(row => {
      const hidden = keys.some(key => {
        const allowed = this.filterCondition[key]
        return allowed.indexOf(row[key]) === -1
      })
      row._showFlg = !hidden
      return row
    })
  }
  /**
   * ヘッダー
   */
  private _header: string[][];
  /**
   * Get accessor of header
   *
   * @readonly
   * @protected
   * @type {string[][]}
   * @memberof FixationTableComponent
   */
  get header(): string[][] {
    return this._header;
  }
  /**
   * Set accessor of header
   *
   * @readonly
   * @protected
   * @type {}
   * @memberof FixationTableComponent
   */
  @Input()
  set header(value: string[][]) {
    this._header = value;
    this.setColgroups();
  }
  /**
   * カラムグループ
   */
  private _colGroup: ITableColGroup[];
  /**
   * Get accessor of column group
   *
   * @readonly
   * @protected
   * @type {ITableColGroup[]}
   * @memberof FixationTableComponent
   */
  get colGroup(): ITableColGroup[] {
    return this._colGroup;
  }
  /**
   * Set accessor of column group
   *
   * @readonly
   * @protected
   * @type {}
   * @memberof FixationTableComponent
   */
  @Input()
  set colGroup(value: ITableColGroup[]) {
    this._colGroup = value;
    this.setColgroups();
  }
  /**
   * 左カラムグループ
   */
  public _leftSideColGroup: ITableColGroup[] = [];
  /**
   * Get accessor of left column group
   *
   * @readonly
   * @protected
   * @type {ITableColGroup[]}
   * @memberof FixationTableComponent
   */
  get leftSideColGroup(): ITableColGroup[] {
    return this._leftSideColGroup;
  }
  /**
   * Set accessor of left column group
   *
   * @readonly
   * @protected
   * @type {}
   * @memberof FixationTableComponent
   */
  set leftSideColGroup(value: ITableColGroup[]) {
    this._leftSideColGroup = value;
  }
  /**
   * Setting left side colgroup
   *
   * @protected
   * @memberof FixationTable
   */
  protected setLeftSideColgroup(): void {}
  /**
   * 右カラムグループ
   */
  public _rightSideColGroup: ITableColGroup[] = [];
  /**
   * Get accessor of right column group
   *
   * @readonly
   * @protected
   * @type {ITableColGroup[]}
   * @memberof FixationTableComponent
   */
  get rightSideColGroup(): ITableColGroup[] {
    return this._rightSideColGroup;
  }
  /**
   * Set accessor of right column group
   *
   * @readonly
   * @protected
   * @type {}
   * @memberof FixationTableComponent
   */
  set rightSideColGroup(value: ITableColGroup[]) {
    this._rightSideColGroup = value;
  }
  /**
   * Setting right side colgroup
   *
   * @protected
   * @memberof FixationTable
   */
  protected setRightSideColgroup(): void {}
  /**
   * Left header layout
   *
   * @memberof FixationTable
   */
  public leftHeaderLayout: any[][] = [];
  /**
   * Right header layout
   *
   * @memberof FixationTable
   */
  public rightHeaderLayout: any[][] = [];
  /**
   * 左アコーディオンカラム
   */
  public leftAcdnCols: { start: number; end: number }[] = [];
  /**
   * 右アコーディオンカラム
   */
  public rightAcdnCols: { start: number; end: number }[] = [];
  
  public leftAllSelected: number[] = [];
  
  public rightAllSelected: number[] = [];
  /**
   * header colSpanFlag
   *
   * @memberof FixationTable
   */
  public colSpanFlag: boolean[] = [];
  /**
   * 子項目識別子
   */
  @Input() childrenKeys: string[];
  /**
   * 固定カラム
   */
  private _fixationIndex: number = 0;
  /**
   * Get accessor of fixation column index
   *
   * @readonly
   * @protected
   * @type {number}
   * @memberof FixationTableComponent
   */
  get fixationIndex(): number {
    return this._fixationIndex;
  }
  /**
   * Set accessor of fixation column index
   *
   * @readonly
   * @protected
   * @type {}
   * @memberof FixationTableComponent
   */
  @Input()
  set fixationIndex(value: number) {
    this._fixationIndex = value;
  }
  /**
   * DisplayDataの開始Index
   */
  private startIndex: number = 0;
  /**
   * Get accessor of sort status
   *
   * @readonly
   * @protected
   * @type {any[]}
   * @memberof FixationTableComponent
   */
  get sortStatus(): any[] {
    return this.sort_status || [];
  }
  /**
   * clickされた行番号
   */
  private _rowSelected: number;
  /**
   * Get accessor of selected row
   *
   * @readonly
   * @protected
   * @type {number}
   * @memberof FixationTableComponent
   */
  get rowSelected() {
    return this._rowSelected;
  }
  
  selected(index: number) {
    this._rowSelected = index;
  }
  /**
   * Static number of max display row
   *
   * @static
   * @memberof FixationTable
   */
  public MAX_DISPLAY_ROW = 300;
  /**
   * Static number of column span default
   *
   * @static
   * @memberof FixationTable
   */
  public COL_SPAN_DEFAULT = 1;
  /**
   * Static number of row span default
   *
   * @static
   * @memberof FixationTable
   */
  public ROW_SPAN_DEFAULT = 1;
  /**
   * Static number of row span default
   *
   * @static
   * @memberof FixationTable
   */
  public ROW_SPAN_EXPAND = 2;
  /**
   * Static number of row height
   *
   * @static
   * @memberof FixationTable
   */
  public ROW_HEIGHT_NORMAL = 60;
  /**
   * Static number of row height(mini)
   *
   * @static
   * @memberof FixationTable
   */
  public ROW_HEIGHT_SMALL = 44;
  /**
   * Static icon of sort ascend
   *
   * @static
   * @memberof FixationTable
   */
  public SORT_ASCEND = "fr-sort-ascend";
  /**
   * Static icon of sort descend
   *
   * @static
   * @memberof FixationTable
   */
  public SORT_DSCEND = "fr-sort-descend";
  /**
   * Static icon of start accordion
   *
   * @static
   * @memberof FixationTable
   */
  public START_ACDN = "fr-chevron-right";
  /**
   * Static icon of end accordion
   *
   * @static
   * @memberof FixationTable
   */
  public END_ACDN = "fr-chevron-left";
  /**
   * Table data
   *
   * @protected
   * @type {*}
   * @memberof BaseFixedTableComponent
   */
  public tableData: any = [];
  /**
   * Total contents row number
   * Default: 0
   * After getting data: according to data size
   *
   * @memberof FixationTable
   */
  public totalContentsRow = 0;
  /**
   * open status of accordion(left)
   */
  public leftOpens: {[index:number]:boolean} = {};
  /**
   * open status of accordion(right)
   */
  public rightOpens: {[index:number]:boolean} = {};
  
  /**
   * OnInit processing
   *
   * @memberof FixationTable
   */
  public ngOnInit() {
    super.ngOnInit();
    if (!this.allSelected) {
      this.allSelected = [];
    }
    this.settingIndicator(true);
  }
  /**
   * ヘッダーアコーディオン表示状態を設定
   * @param acdnIndex number
   * @param open boolean
   * @param acdn {start:number, end:number}[]
   */
  setAcdnStatus(
    acdnIndex: number,
    open: boolean,
    acdn: { start: number; end: number }[],
    colGroup: ITableColGroup[],
    right: boolean
  ) {
    let acdnCol = { start: 0, end: 0 };
    if (!!right) {
      this.rightHeaderLayout = [];
    } else {
      this.leftHeaderLayout = [];
    }
    acdn.forEach(c => {
      if (!!open) {
        if (c.start === acdnIndex) {
          acdnCol = c;
          if (!right) {
            this.leftOpens[c.start] = true;
          } else {
            this.rightOpens[c.start] = true;
          }
        }
      } else {
        if (c.end === acdnIndex) {
          acdnCol = c;
          if (!right) {
            this.leftOpens[c.start] = false;
          } else {
            this.rightOpens[c.start] = false;
          }
        }
      }
    });
    colGroup.map(c => {
      if (!!open && acdnCol.start < c.index && acdnCol.end >= c.index) {
        c.conditions = true;
      } else if (!open && acdnCol.start < c.index && acdnCol.end >= c.index) {
        c.conditions = false;
      }
    });
    this.setHeaderLayout();
    if (!right) {
      this._leftWidth = 0;
      colGroup.forEach(col => {
        if (col.conditions && !!col.width) {
          this._leftWidth += Number(col.width.replace(/([^\d]+)/, ''));
        }
      })
    } else {
      this._rightWidth = 0;
      colGroup.forEach(col => {
        if (col.conditions && !!col.width) {
          this._rightWidth += Number(col.width.replace(/([^\d]+)/, ''));
        }
      })
    }
    this.eventHub.next({
      id: this.id + "__hAcdn",
      type: "click",
      source: {},
      index: acdnIndex,
    })
  }
  
  /**
   * Setting colgroup
   *
   * @memberof FixationTable
   */
  public setColgroups() {
    this.timeUtil.timeout(0).subscribe(() => {
      this.leftSideColGroup = [];
      this.rightSideColGroup = [];
      this.leftHeaderLayout = [];
      this.rightHeaderLayout = [];
      this.leftChildren = [];
      this.rightChildren = [];
      this.leftOpens = {};
      this.rightOpens = {};
      this._leftWidth = 0;
      this._rightWidth = 0;
      if (!!this._fixationIndex) {
        this.colGroup.forEach((c, index) => {
          if (this._fixationIndex >= index + 1) {
            if (c.conditions && !!c.width) {
              this._leftWidth += Number(c.width.replace(/([^\d]+)/, ''));
              this._leftUnit = c.width.replace(/([\d]+)/, '')
            }
            this._leftSideColGroup.push(c);
          } else {
            if (c.conditions && !!c.width) {
              this._rightWidth += Number(c.width.replace(/([^\d]+)/, ''));
              this._rightUnit = c.width.replace(/([\d]+)/, '')
            }
            this._rightSideColGroup.push(c);
          }
        });
      } else {
        this.colGroup.forEach((c, index) => {
          if (c.conditions && !!c.width) {
            this._rightWidth += Number(c.width.replace(/([^\d]+)/, ''));
            this._rightUnit = c.width.replace(/([\d]+)/, '')
          }
        });
        this._rightSideColGroup = this.colGroup;
      }
      this.children.forEach((c, index) => {
        if (this._fixationIndex - 1 >= index) {
          this.leftChildren.push(c);
        } else {
          this.rightChildren.push(c);
        }
      });
      this.setAcdnCols();
      this.setHeaderLayout();
    });
  }
  /**
   * Setting accordion cols
   *
   * @memberof FixationTable
   */
  public setAcdnCols() {
    let startAcdn: number = 0;
    let endAcdn: number = 0;
    this.colGroup.forEach((t, index) => {
      if (!!t.startAcdn) {
        startAcdn = index + 1;
      } else if (!!t.endAcdn) {
        endAcdn = index + 1;
      }
      if (startAcdn !== 0 && endAcdn !== 0) {
        let value = {
          start: startAcdn,
          end: endAcdn
        };
        if (this._fixationIndex >= startAcdn) {
          this.leftAcdnCols.push(value);
        } else {
          this.rightAcdnCols.push(value);
        }
        startAcdn = 0;
        endAcdn = 0;
      }
    });
  }
  /**
   * Setting header layout
   *
   * @memberof FixationTable
   */
  public setHeaderLayout() {
    this.colSpanFlag = [];
    let colSpanIndex: number = 1;
    let rowSpanIndex: number = 1;
    let filterList = this.colGroup.filter(c => {
      return !!c.conditions;
    });
    let filterHeaderList: string[][] = [];
    let fixedIndex = this._fixationIndex;
    this.header.forEach(h => {
      let target = h.filter((h, index) => {
        return !!this.colGroup[index] && !!this.colGroup[index].conditions;
      });
      filterHeaderList.push(target);
    });
    filterHeaderList.forEach((h, parentIndex) => {
      let targetList: IHeaderLayout[] = [];
      let leftTargetList: IHeaderLayout[] = [];
      let rightTargetList: IHeaderLayout[] = [];
      h.forEach((current, currentIndex) => {
        let value: IHeaderLayout;
        if (
          !!current &&
          current === h[currentIndex + 1] &&
          this.header.length !== parentIndex + 1
        ) {
          colSpanIndex++;
        } else {
          let parentFlag: boolean = false;
          filterHeaderList.forEach((h, index) => {
            if (!h[currentIndex]) {
              rowSpanIndex++;
            }
          });
          if (
            this.header.length !== parentIndex + 1 &&
            rowSpanIndex === this.ROW_SPAN_DEFAULT
          ) {
            value = {
              colIndex: filterList[currentIndex].index,
              colSpan: colSpanIndex,
              rowSpan: rowSpanIndex,
              title: current,
              sort: false,
              startAcdn: false,
              endAcdn: false,
              parent: true,
              all: false
            };
          } else {
            value = {
              colIndex: filterList[currentIndex].index,
              colSpan: this.COL_SPAN_DEFAULT,
              rowSpan: rowSpanIndex,
              title: current,
              sort: filterList[currentIndex].sort,
              startAcdn: filterList[currentIndex].startAcdn,
              endAcdn: filterList[currentIndex].endAcdn,
              parent: false,
              all: !!this.allSelected.some(
                a => a === filterList[currentIndex].index
              )
                ? true
                : false
            };
          }
          targetList.push(value);
          colSpanIndex = 1;
          rowSpanIndex = 1;
        }
      });
      targetList.forEach((t, index) => {
        if (fixedIndex >= t.colIndex) {
          if (!!t.title) {
            leftTargetList.push(t);
          }
        } else {
          if (!!t.title) {
            rightTargetList.push(t);
          }
        }
      });
      this.leftHeaderLayout.push(leftTargetList);
      this.rightHeaderLayout.push(rightTargetList);
    });
  }
  /**
   * コンポーネント内で発生したイベントを通知する。
   * @param eventType イベント種別
   * @param index 序数(リスト項目の場合のみ)
   */
  on(eventType: string | { type: string }, index?: number) {
    const type = typeof eventType === "string" ? eventType : eventType.type;
    this.eventHub.next({
      id: this.id,
      type: type,
      source: {},
      index: index
    });
  }
  /**
   * Load data
   *
   * @private
   * @memberof FixationTable
   */
  private loadData(event?: ChangeEvent) {
    this.timeUtil.timeout(0).subscribe(() => {
      if (this.isLazyload(event.end)) {
        this.on("scroll_end");
      }
    });
  }
  /**
   * update the start index of display data
   *
   * @private
   * @memberof FixationTable
   */
  private updateStartIndex(event?: ChangeEvent) {
    if (!!event && event.start !== undefined) {
      this.startIndex = event.start;
    }
  }
  /**
   * Determine whether it is target of lazyload
   *
   * @private
   * @param {number} end
   * @returns
   * @memberof FixationTable
   */
  private isLazyload(end: number): boolean {
    return (
      end === this.tableData.length &&
      this.tableData.length === this.MAX_DISPLAY_ROW
    );
  }
  /**
   * Sort data
   *
   * @param {string} target
   * @memberof FixationTable
   */
  public sortData(target?: string) {
    if (!target && (!this.sortStatus || this.sortStatus.length === 0)) return;
    if (!!target) {
      const targetIndex = this.sortStatus.findIndex(s => s.key == target);
      var targetOrder = true;
      if (targetIndex > -1) {
        targetOrder = !this.sortStatus[targetIndex].sort_order;
        this.sortStatus.splice(targetIndex, 1);
      }
      this.sortStatus.unshift({ key: target, sort_order: targetOrder });
    }
    if (this.sortStatus.length > 0) {
      this.tableData.sort((a: any, b: any) => this.compare(a, b, 0));
    }
  }
  
  /**
   * compare function
   *
   * @param {any} a, {any} b, {number} index
   * @memberof FixationTable
   */
  private compare(a: any, b: any, index: number): number {
    if (index >= this.sortStatus.length) return 0;
    let status = this.sortStatus[index];
    let coefficient: number;
    if (status.sort_order) {
      coefficient = 1; // ascending
    } else {
      coefficient = -1; // descending
    }
    if (isNaN(a[status.key]) || isNaN(b[status.key])) {
      if (a[status.key] < b[status.key]) {
        return -1 * coefficient;
      } else if (a[status.key] > b[status.key]) {
        return 1 * coefficient;
      } else {
        return this.compare(a, b, index + 1);
      }
    } else {
      if (Number(a[status.key]) < Number(b[status.key])) {
        return -1 * coefficient;
      } else if (Number(a[status.key]) > Number(b[status.key])) {
        return 1 * coefficient;
      } else {
        return this.compare(a, b, index + 1);
      }
    }
  }
  /**
   * Order icon of item
   *
   * @param {string} target
   * @memberof FixationTable
   */
  public itemOrderIcon(target: string): boolean {
    let i = this.sortStatus.findIndex(s => s.key == target);
    return !!this.sortStatus[i] && this.sortStatus[i].sort_order;
  }
  
  /**
   * Determine whether the target is sortable
   *
   * @param {string} target
   * @returns
   * @memberof FixationTable
   */
  public isSortable(target: string): boolean {
    const sortableItems: string[] = [];
    this.sort_status.forEach(s => {
      sortableItems.push(s.key);
    });
    return !!sortableItems && sortableItems.indexOf(target) > -1;
  }
  /**
   * Calculate the width of left table
   */
  tableLeftWidth(): string {
    return !!this._leftWidth ? this._leftWidth + this._leftUnit : '37%';
  }
  /**
   * Calculate the width of right table
   */
  tableRightWidth(): string {
    return !!this._leftWidth ? (100 - this._leftWidth) + this._leftUnit : '100%';
  }
  /**
   * Calculate the width of left table's column
   */
  leftColWidth(width: string): string {
    return !!this._leftWidth ? Number(width.replace(/([^\d]+)/, ''))/this._leftWidth*100 + '%' : width
  }
  /**
   * Get row height
   */
  rowHeight():number {
    return !!this.showSmallHeight ? this.ROW_HEIGHT_SMALL : this.ROW_HEIGHT_NORMAL
  }
  /**
   * Get virtual-scroll's height
   */
  virtualHeight():string {
    return (this.tableData.filter(d => d._showFlg !== false).length * this.rowHeight() + 1) + 'px'
  }
  /**
   * Get virtual-scroll's max-height
   */
  virtualMaxHeight():string {
    return !!this.displayRowCount ? (this.displayRowCount * this.rowHeight() + 1) + 'px' : null
  }
  /**
   * Calculate the scrollable width of right table
   */
  tableRightWidthAll():string {
    return !!this._rightWidth ? this._rightWidth + this._rightUnit : '100%';
  }
  
  /**
   * AfterViewInit
   */
  ngAfterViewInit() {
    this.setScrollBar();
  }
  
  /**
   * リサイズ
   */
  @HostListener('window:resize')
  onResize() {
    this.setScrollBar();
  }
  
  /**
   * スクロールバー設定
   */
  setScrollBar() {
    this.setScrollBarHeight();
    this.setScrollBarWidth();
  }
  
  /**
   * バーチャルスクロールエレメント
   */
  @ViewChild('virtualScroll') virtualScroll: any;
  
  /**
   * 右下横スクロールバーエレメント
   */
  @ViewChild('rightBottomScrollBar') rightBottomScrollBar: ElementRef;
  
  /**
   * スクロールバー高さ
   */
  _scrollBarHeight: string;
  
  /**
   * スクロールバー高さ get accessor
   */
  get scrollBarHeight() {
    return this._scrollBarHeight;
  }
  
  /**
   * スクロールバー高さ set function
   */
  setScrollBarHeight(): any {
    this.timeUtil.timeout(0).subscribe(() => {
      if (this.bottomRight && this.bottomRight.nativeElement) {
        this._scrollBarHeight = (this.bottomRight.nativeElement.offsetHeight - this.bottomRight.nativeElement.clientHeight) + 'px';
      }
    });
  }
  
  /**
   * スクロールバー幅
   */
  _scrollBarWidth: string;
  
  /**
   * スクロールバー幅 get accessor
   */
  get scrollBarWidth() {
    return this._scrollBarWidth;
  }
  
  /**
   * スクロールバー幅 set function
   */
  setScrollBarWidth(): any {
    this.timeUtil.timeout(0).subscribe(() => {
      if (this.virtualScroll && this.virtualScroll.element.nativeElement) {
        this._scrollBarWidth = (this.virtualScroll.element.nativeElement.offsetWidth - this.virtualScroll.element.nativeElement.clientWidth) + 'px';
      }
    });
  }
  
  /**
   * 横スクロール処理
   */
  scrollForXDirection($event: Event, byScrollBar: boolean) {
    super.onScrollForXDirection($event)
    if (!byScrollBar && this.rightBottomScrollBar) {
      this.ngZone.runOutsideAngular(() => {
        this.rightBottomScrollBar.nativeElement.scrollLeft = this.bottomRight.nativeElement.scrollLeft;
      });
    }
  }
}


// WEBPACK FOOTER //
// ./src/widgets/table/fixation-table/fixation-table.component.ts